// Date and time values, shared by StuiDateTime (input) and formatDateTime (display).
//
// The rule: the database stores timestamps in UTC; people see and enter them in their own zone;
// calendar dates and times of day are never zone-shifted. What decides how a value is handled is
// the real SQL column type, so every caller says which kind of value it holds:
//
//   valueZone 'utc'   a datetime / datetime2 column: an instant. The API sends ISO with Z
//                     ("2026-10-03T02:00:00.000Z"); a zoneless string is read as UTC too, so an
//                     older endpoint that drops the Z still shows the right time. Shown and typed
//                     in displayZone, converted back to UTC on the way out.
//   valueZone 'local' a wall-clock time with no zone ("2026-10-03T22:00"). Shown as-is.
//   valueZone 'none'  a date column ("2026-10-03") or time column ("22:00"). Read as text and
//                     never passed through new Date(): a due date of Oct 3 is Oct 3 in every zone.
//
// displayZone 'local' means the person's zone: the Timezone preference (display.timezone) when it
// names one, otherwise the browser's. An explicit IANA name ("America/Chicago") overrides it.

const pad = (n, w = 2) => String(n).padStart(w, '0');

// The person's chosen zone; null = follow the browser. Set from the display.timezone preference,
// which PreferencesContext mirrors to localStorage so the first paint already uses it.
export const TIME_ZONE_MIRROR_KEY = 'dawn.timeZone';
let userZone = null;

/** Applies the display.timezone preference: an IANA zone, or 'auto' / empty for the browser's. */
export const setUserTimeZone = (zone) => {
  userZone = zone && zone !== 'auto' && isValidZone(zone) ? zone : null;
};

/** The browser's own zone, e.g. "America/Chicago". */
export const browserTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

/** The zone 'local' resolves to right now: the preference when set, else the browser's. */
export const effectiveTimeZone = () => userZone || browserTimeZone();

function isValidZone(zone) {
  try { new Intl.DateTimeFormat('en-US', { timeZone: zone }); return true; } catch { return false; }
}

try { setUserTimeZone(localStorage.getItem(TIME_ZONE_MIRROR_KEY)); } catch { /* storage blocked: follow the browser */ }

const zoneOption = (displayZone) => {
  if (displayZone && displayZone !== 'local') return displayZone;
  return userZone || undefined;
};

// A zoneless date-time string: "2026-10-03T02:00:00", "2026-10-03 02:00:00.123", "2026-10-03T02:00".
const ZONELESS = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(\.\d+)?)?$/;

/**
 * A stored UTC value as a Date, or null. Accepts a Date, epoch milliseconds, ISO with Z or an
 * offset, and a zoneless string, which is read as UTC (never as the browser's local time).
 */
export const parseUtc = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  const s = String(value).trim();
  const m = ZONELESS.exec(s);
  const d = m ? new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] || '00'}${m[7] ? m[7].slice(0, 4) : ''}Z`) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Intl.DateTimeFormat is expensive to construct and cheap to reuse, and a grid or PDF formats tens
// of thousands of cells, so one formatter per zone (and kind) is kept.
const formatters = new Map();
const formatterFor = (kind, zone) => {
  const key = `${kind}|${zone || ''}`;
  let f = formatters.get(key);
  if (!f) {
    f = kind === 'parts'
      ? new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      : new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' });
    formatters.set(key, f);
  }
  return f;
};

/** { year, month, day, hour, minute } of an instant, as the clock reads in displayZone. */
export const partsInZone = (date, displayZone = 'local') => {
  const p = { year: 0, month: 0, day: 0, hour: 0, minute: 0 };
  for (const x of formatterFor('parts', zoneOption(displayZone)).formatToParts(date)) {
    if (x.type in p) p[x.type] = +x.value;
  }
  return p;
};

/** The instant at which displayZone's clock reads these parts. Second pass settles DST edges. */
export const instantFromZoneParts = ({ year, month, day, hour, minute }, displayZone = 'local') => {
  if (!zoneOption(displayZone)) return new Date(year, month - 1, day, hour, minute);
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  const offsetAt = (t) => {
    const p = partsInZone(new Date(t), displayZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - t;
  };
  let t = asUtc - offsetAt(asUtc);
  t = asUtc - offsetAt(t);
  return new Date(t);
};

/** Short zone name for displayZone at an instant: "EDT", "CST". */
export const zoneAbbrev = (date = new Date(), displayZone = 'local') => {
  const p = formatterFor('zone', zoneOption(displayZone)).formatToParts(date).find(x => x.type === 'timeZoneName');
  return p ? p.value : '';
};

const literalDate = (value) => /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));

/**
 * A stored value -> the parts a person sees, or null when empty or unparseable.
 * Parts: { year, month, day, hour, minute }, any of which may be absent for the mode.
 */
export const valueToParts = (value, { mode = 'datetime', valueZone = 'local', displayZone = 'local' } = {}) => {
  if (value === null || value === undefined || value === '') return null;
  if (mode === 'time' && valueZone !== 'utc') {
    // A time column: "22:00", "22:00:00", or an older ISO stamp carrying it ("1900-01-01T22:00:00").
    const m = /(?:^|T)(\d{2}):(\d{2})/.exec(String(value));
    return m ? { hour: +m[1], minute: +m[2] } : null;
  }
  if (valueZone === 'utc') {
    const d = parseUtc(value);
    if (!d) return null;
    const p = partsInZone(d, displayZone);
    if (mode === 'date') return { year: p.year, month: p.month, day: p.day };
    if (mode === 'time') return { hour: p.hour, minute: p.minute };
    return p;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(String(value));
  if (!m) return null;
  const parts = { year: +m[1], month: +m[2], day: +m[3] };
  if (mode === 'datetime') {
    if (m[4] === undefined) return null;
    parts.hour = +m[4];
    parts.minute = +m[5];
  }
  return parts;
};

/** Parts -> the stored value for the mode and zone. Callers pass complete parts only. */
export const partsToValue = (parts, { mode = 'datetime', valueZone = 'local', displayZone = 'local' } = {}) => {
  if (mode === 'time') return `${pad(parts.hour)}:${pad(parts.minute)}`;
  const date = `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}`;
  if (mode === 'date') return date;
  if (valueZone === 'utc') return instantFromZoneParts(parts, displayZone).toISOString();
  return `${date}T${pad(parts.hour)}:${pad(parts.minute)}`;
};

export const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

/** Now, as parts in displayZone. */
export const nowParts = (displayZone = 'local') => partsInZone(new Date(), displayZone);

/** Today's date in displayZone as 'YYYY-MM-DD'. */
export const todayKey = (displayZone = 'local') => {
  const p = nowParts(displayZone);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
};

/**
 * The calendar day a value falls on, as 'YYYY-MM-DD', or ''. A 'utc' value is placed on the
 * person's local day; a 'none' value is its own literal date. Grouping, day filters and the
 * Year > Month > Day filter tree all key on this, so they agree with what the cell shows.
 */
export const dayKey = (value, { valueZone = 'utc', displayZone = 'local' } = {}) => {
  const p = valueToParts(value, { mode: 'date', valueZone, displayZone });
  return p ? `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}` : '';
};

/**
 * A local calendar day -> the UTC instants that bound it: { from, to } as ISO with Z, where
 * `to` is the start of the next local day (exclusive). A DST day is 23 or 25 hours long, so the
 * end is computed from the next day's midnight, never by adding 24 hours.
 */
export const localDayToUtcRange = (day, displayZone = 'local') => {
  const m = literalDate(day);
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3];
  const next = new Date(Date.UTC(y, mo - 1, d + 1));
  const from = instantFromZoneParts({ year: y, month: mo, day: d, hour: 0, minute: 0 }, displayZone);
  const to = instantFromZoneParts({ year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate(), hour: 0, minute: 0 }, displayZone);
  return { from: from.toISOString(), to: to.toISOString() };
};

/** A range of local days ('YYYY-MM-DD', either end optional) -> UTC bounds { from, to } (to exclusive). */
export const localDaysToUtcRange = (fromDay, toDay, displayZone = 'local') => ({
  from: fromDay ? localDayToUtcRange(fromDay, displayZone)?.from ?? null : null,
  to: toDay ? localDayToUtcRange(toDay, displayZone)?.to ?? null : null,
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * A stored value as text.
 *   formatDateTime(row.ChangedAt, { valueZone: 'utc' })              -> "10/03/2026 10:00 PM"
 *   formatDateTime('2026-10-03', { mode: 'date', valueZone: 'none' }) -> "10/03/2026"
 *   formatDateTime(v, { valueZone: 'utc', style: 'long' })            -> "Sat, Oct 3, 2026, 10:00 PM"
 * Options: mode ('datetime' | 'date' | 'time'), valueZone, displayZone, style ('numeric', the
 * default, or 'long'), showZone (append "EDT"), timeIfToday (drop today's date), seconds (numeric
 * style only), weekday (long style; default true). Empty or unparseable values return ''.
 */
export const formatDateTime = (value, {
  mode = 'datetime', valueZone = 'local', displayZone = 'local', style = 'numeric',
  showZone = false, timeIfToday = false, weekday = true, seconds = false,
} = {}) => {
  const p = valueToParts(value, { mode, valueZone, displayZone });
  if (!p) return '';
  let sec = '';
  if (seconds && mode !== 'date' && valueZone === 'utc') {
    const d = parseUtc(value);
    if (d) sec = `:${pad(d.getUTCSeconds())}`;
  }
  const time = mode === 'date' ? '' : `${p.hour % 12 || 12}:${pad(p.minute)}${sec} ${p.hour < 12 ? 'AM' : 'PM'}`;
  // The zone label and "today" are only worked out when asked for: this runs once per grid cell.
  let zone = '';
  if (showZone && mode !== 'date') {
    let instant = null;
    if (valueZone === 'utc') instant = parseUtc(value);
    else if (mode !== 'time') instant = instantFromZoneParts({ hour: 12, minute: 0, ...p }, displayZone);
    zone = ` ${zoneAbbrev(instant || new Date(), displayZone)}`;
  }
  if (mode === 'time') return `${time}${zone}`;

  if (mode === 'datetime' && timeIfToday) {
    const today = nowParts(displayZone);
    if (today.year === p.year && today.month === p.month && today.day === p.day) return `${time}${zone}`;
  }
  if (style === 'long') {
    // Weekday from a noon-UTC date so the zone cannot move it to a neighbouring day.
    const wd = WEEKDAYS[new Date(Date.UTC(p.year, p.month - 1, p.day, 12)).getUTCDay()];
    const date = `${weekday ? `${wd}, ` : ''}${MONTHS[p.month - 1]} ${p.day}, ${p.year}`;
    return mode === 'date' ? date : `${date}, ${time}${zone}`;
  }
  const date = `${pad(p.month)}/${pad(p.day)}/${pad(p.year, 4)}`;
  return mode === 'date' ? date : `${date} ${time}${zone}`;
};

/** A UTC timestamp (datetime / datetime2 column) in the person's zone: "10/03/2026 10:00 PM". */
export const formatTimestamp = (value, options = {}) => formatDateTime(value, { valueZone: 'utc', ...options });

/** A date column, literally: "10/03/2026". Never zone-shifted. */
export const formatDateOnly = (value, options = {}) => formatDateTime(value, { mode: 'date', valueZone: 'none', ...options });

/** A UTC timestamp's local calendar date: "10/03/2026". */
export const formatTimestampDate = (value, options = {}) => formatDateTime(value, { mode: 'date', valueZone: 'utc', ...options });

/** Epoch milliseconds for sorting / comparing UTC values (0 when empty or unparseable). */
export const utcMillis = (value) => parseUtc(value)?.getTime() ?? 0;

/** "Generated" stamps on exports and printouts: local time with the zone, "10/03/2026 10:00 PM EDT". */
export const generatedStamp = (date = new Date()) => formatDateTime(date, { valueZone: 'utc', showZone: true });
